/**
 * DealDeskHub — Sprint-DealDesk Loop 13 (full redesign)
 *
 * New design: Custom CSS design system, 4-page internal routing (Dashboard / Portfolio /
 * Pipeline / Escalations), full-page deal detail, Kanban drag-to-stage, escalation cards,
 * coordinator actions with real API calls.
 *
 * Auth: deal-coordinator or admin Cognito group
 */

import { useState, useEffect, useCallback, useContext, createContext, useRef } from 'react';
import {
  Folder, Flag, AlertTriangle, Clock, Check, X,
  ChevronRight, ChevronLeft, Plus, Mail, Edit,
  ArrowLeftRight, Send, MapPin, StickyNote, History, Grid2x2,
  AlignJustify, Table, Download, Zap, TrendingUp, RefreshCw,
  Search, Bell, Lock, Activity, CheckCircle2,
} from 'lucide-react';
import { apiFetch } from '../lib/external-api-config';
import tokenStorage from '../services/token-storage';
import '../styles/dealdesk.css';
const spLogo = new URL('../assets/834893d5dd876e7116ce18188b05850f72b96514.png', import.meta.url).href;

// ─── SA Stages ───────────────────────────────────────────────────────────────

const STAGES = [
  { id: 'offer-made',         label: 'Offer Made',         hue: 250 },
  { id: 'offer-accepted',     label: 'Offer Accepted',     hue: 220 },
  { id: 'otp-signed',         label: 'OTP Signed',         hue: 200 },
  { id: 'bond-application',   label: 'Bond Application',   hue: 175 },
  { id: 'bond-granted',       label: 'Bond Granted',       hue: 150 },
  { id: 'attorney-appointed', label: 'Attorney Appointed', hue: 120 },
  { id: 'fica-complete',      label: 'FICA Complete',      hue: 90  },
  { id: 'transfer-lodged',    label: 'Transfer Lodged',    hue: 55  },
  { id: 'registered',         label: 'Registered',         hue: 145 },
];

// Fallback labels for stages from the old schema
const STAGE_FALLBACK: Record<string, { label: string; hue: number }> = {
  'lead':               { label: 'Lead',               hue: 250 },
  'viewing-scheduled':  { label: 'Viewing Scheduled',  hue: 220 },
  'offer-made':         { label: 'Offer Made',          hue: 200 },
  'offer-accepted':     { label: 'Offer Accepted',      hue: 175 },
  'otp-signed':         { label: 'OTP Signed',          hue: 165 },
  'bond-application':   { label: 'Bond Application',    hue: 150 },
  'bond-granted':       { label: 'Bond Granted',        hue: 140 },
  'attorney-appointed': { label: 'Attorney Appointed',  hue: 120 },
  'fica-complete':      { label: 'FICA Complete',       hue: 90  },
  'transfer-lodged':    { label: 'Transfer Lodged',     hue: 55  },
  'financing':          { label: 'Financing',           hue: 150 },
  'inspection':         { label: 'Inspection',          hue: 200 },
  'closing':            { label: 'Closing',             hue: 55  },
  'registered':         { label: 'Registered',          hue: 145 },
};

function getStage(id: string) {
  return STAGES.find(s => s.id === id) || STAGE_FALLBACK[id] ? { id, ...STAGE_FALLBACK[id] } : { id, label: id, hue: 250 };
}

function stageColor(hue: number) {
  return `oklch(0.62 0.13 ${hue})`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface RawDeal {
  dealId: string;
  status: string;
  stageLabel?: string;
  agentId: string;
  agentName: string;
  clientName: string;
  clientEmail?: string;
  agentEmail?: string;
  propertyAddress: string;
  offerAmount: number;
  stagnant: boolean;
  stagnantSince?: string;
  stagnantDays: number;
  createdAt: number;
  updatedAt: number;
}

interface Deal extends RawDeal {
  stageLabel: string;
  stageHue: number;
  agentHue: number;
  atRisk: boolean;
  stageAgeDays: number;
  suburb: string;
  city: string;
  auditLog: AuditEntry[];
  coordinatorNotes: NoteEntry[];
}

interface AuditEntry {
  id: string;
  type: string;
  actor: string;
  actorRole: string;
  timestamp: number;
  details: {
    reason?: string;
    from?: string;
    to?: string;
    subject?: string;
    target?: string;
    note?: string;
    preview?: string;
  };
}

interface NoteEntry {
  id: string;
  author: string;
  timestamp: number;
  text: string;
}

interface AgentOption {
  agentId: string;
  name: string;
  email?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function agentHue(agentId: string): number {
  let h = 0;
  for (let i = 0; i < agentId.length; i++) h = (h * 31 + agentId.charCodeAt(i)) & 0xffff;
  return h % 360;
}

function parseAddress(addr: string): { suburb: string; city: string } {
  const parts = addr.split(',').map(p => p.trim());
  if (parts.length >= 3) return { suburb: parts[parts.length - 2], city: parts[parts.length - 1] };
  if (parts.length === 2) return { suburb: parts[0], city: parts[1] };
  return { suburb: addr, city: '' };
}

function augmentDeal(raw: RawDeal): Deal {
  const stage = getStage(raw.status);
  const stageAgeDays = raw.updatedAt ? Math.floor((Date.now() - raw.updatedAt) / 86400000) : 0;
  const { suburb, city } = parseAddress(raw.propertyAddress || '');
  return {
    ...raw,
    stageLabel: raw.stageLabel || stage.label,
    stageHue: stage.hue,
    agentHue: agentHue(raw.agentId || ''),
    atRisk: !raw.stagnant && stageAgeDays >= 5,
    stageAgeDays,
    suburb,
    city,
    auditLog: [],
    coordinatorNotes: [],
  };
}

function normalizeAuditEntry(raw: any, idx: number): AuditEntry {
  // API shape: { action, actorName, timestamp, previousStatus, newStatus, previousAgentId, newAgentId, reason, recipient, subject, channel }
  // Design shape: { id, type, actor, actorRole, timestamp, details }
  const type = raw.type || raw.action || 'note';
  const details: AuditEntry['details'] = {};
  if (raw.reason) details.reason = raw.reason;
  if (raw.previousStatus && raw.newStatus) { details.from = raw.previousStatus; details.to = raw.newStatus; }
  if (raw.from && raw.to) { details.from = raw.from; details.to = raw.to; }
  if (raw.subject) details.subject = raw.subject;
  if (raw.recipient || raw.target) details.target = raw.recipient || raw.target;
  if (raw.note) details.note = raw.note;
  if (raw.preview) details.preview = raw.preview;
  return {
    id: raw.id || `au-${idx}`,
    type,
    actor: raw.actor || raw.actorName || 'Coordinator',
    actorRole: raw.actorRole || 'Deal Coordinator',
    timestamp: raw.timestamp,
    details,
  };
}

function normalizeNote(raw: any, idx: number): NoteEntry {
  return {
    id: raw.id || `n-${idx}`,
    author: raw.author || raw.coordinatorName || 'Coordinator',
    timestamp: raw.timestamp,
    text: raw.text || raw.note || '',
  };
}

function formatZAR(n: number) {
  return 'R\u00A0' + n.toLocaleString('en-ZA');
}
function formatZARShort(n: number) {
  if (n >= 1_000_000) return 'R\u00A0' + (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return 'R\u00A0' + (n / 1_000).toFixed(0) + 'k';
  return 'R\u00A0' + n;
}
function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((s: string) => s[0]).join('').toUpperCase();
}
function timeAgo(ts: number) {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return min + 'm ago';
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr + 'h ago';
  const day = Math.floor(hr / 24);
  if (day < 30) return day + 'd ago';
  return Math.floor(day / 30) + 'mo ago';
}
function fullDate(ts: number) {
  const d = new Date(ts);
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' +
         d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ─── API ─────────────────────────────────────────────────────────────────────

async function apiCall(path: string, options?: RequestInit) {
  const token = tokenStorage.getIdToken() || '';
  const res = await apiFetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options?.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || `API error ${res.status}`);
  }
  return res.json();
}

// ─── Toast context ────────────────────────────────────────────────────────────

interface ToastCtxType { push: (msg: string) => void }
const ToastCtx = createContext<ToastCtxType>({ push: () => {} });

function DDToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<{ id: string; msg: string }[]>([]);
  const push = useCallback((msg: string) => {
    const id = Math.random().toString(36).slice(2);
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="dd-toasts">
        {toasts.map(t => (
          <div className="dd-toast" key={t.id}>
            <CheckCircle2 size={14} />
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
function useToast() { return useContext(ToastCtx).push; }

// ─── Shared components ────────────────────────────────────────────────────────

function StageChip({ status }: { status: string }) {
  const s = getStage(status);
  return (
    <span className="stage-chip">
      <span className="stage-dot" style={{ background: stageColor(s.hue) }}></span>
      {s.label}
    </span>
  );
}

function StatusPill({ deal, lg }: { deal: Deal; lg?: boolean }) {
  const cls = lg ? ' lg' : '';
  if (deal.stagnant) return <span className={'pill stagnant' + cls}><span className="dot"></span>Stagnant · {deal.stagnantDays}d</span>;
  if (deal.atRisk) return <span className={'pill risk' + cls}><span className="dot"></span>At risk · {deal.stageAgeDays}d</span>;
  return <span className={'pill good' + cls}><span className="dot"></span>On track</span>;
}

function AgentAvatar({ name, hue, size = 22 }: { name: string; hue: number; size?: number }) {
  return (
    <span className="dc-avatar"
          style={{ width: size, height: size, fontSize: size * 0.45,
                   background: `linear-gradient(135deg, oklch(0.75 0.10 ${hue}), oklch(0.55 0.16 ${hue}))` }}>
      {initials(name)}
    </span>
  );
}

function Sparkline({ values, w = 240, h = 28, color = 'var(--accent)' }: { values: number[]; w?: number; h?: number; color?: string }) {
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => [i * step, h - ((v - min) / range) * (h - 4) - 2]);
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${path} L${w},${h} L0,${h} Z`;
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path className="area" d={area} fill={color} opacity="0.12" stroke="none" />
      <path d={path} stroke={color} fill="none" strokeWidth="1.5" />
    </svg>
  );
}

// ─── Nav ─────────────────────────────────────────────────────────────────────

type Route = 'dashboard' | 'portfolio' | 'pipeline' | 'escalations' | `deal/${string}`;

function NavTop({ route, setRoute, search, setSearch, onBack }: {
  route: Route; setRoute: (r: Route) => void; search: string;
  setSearch: (s: string) => void; onBack: () => void;
}) {
  const items = [
    { id: 'dashboard',   label: 'Dashboard' },
    { id: 'portfolio',   label: 'Portfolio' },
    { id: 'pipeline',    label: 'Pipeline' },
    { id: 'escalations', label: 'Escalations' },
  ] as const;
  const activeId = route.split(/[/?#]/)[0];
  return (
    <header className="topnav">
      <div className="brand">
        <img src={spLogo} alt="Something Property" className="sp-logo" />
      </div>
      <div className="topnav-items">
        {items.map(it => (
          <div key={it.id}
               className={'nav-item' + (activeId === it.id ? ' active' : '')}
               onClick={() => setRoute(it.id)}>
            {it.label}
          </div>
        ))}
      </div>
      <div className="topnav-right">
        <div className="input-wrap" style={{ width: 260 }}>
          <Search className="input-icon" size={14} />
          <input className="dd-input" placeholder="Search deals, clients, addresses…"
                 value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="icon-btn" onClick={onBack} title="Back to app" style={{ marginLeft: 4 }}>
          <X size={16} />
        </button>
      </div>
    </header>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────────

function Modal({ title, sub, onClose, children, footer }: {
  title: string; sub?: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <div>
            <h3 className="modal-title">{title}</h3>
            {sub && <p className="modal-sub">{sub}</p>}
          </div>
          <button className="icon-btn" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

function ReassignModal({ deal, agents, onClose, onConfirm }: {
  deal: Deal; agents: AgentOption[]; onClose: () => void;
  onConfirm: (d: { agentId: string; reason: string }) => void;
}) {
  const [agentId, setAgentId] = useState('');
  const [reason, setReason] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const valid = agentId && reason.trim().length > 0;
  const filtered = agents.filter(a => a.agentId !== deal.agentId &&
    (a.name.toLowerCase().includes(query.toLowerCase()) || (a.email || '').toLowerCase().includes(query.toLowerCase())));
  const selected = agents.find(a => a.agentId === agentId);
  return (
    <Modal title="Reassign deal" sub={`Move ${deal.dealId} — ${deal.clientName} to a different agent`} onClose={onClose}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!valid} onClick={() => onConfirm({ agentId, reason })}>
          <ArrowLeftRight size={13} /> Reassign deal
        </button></>}>
      <div className="field">
        <label className="field-label">Current agent</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--line)' }}>
          <AgentAvatar name={deal.agentName} hue={deal.agentHue} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{deal.agentName}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{deal.agentEmail}</div>
          </div>
        </div>
      </div>
      <div className="field">
        <label className="field-label">New agent</label>
        <div className="combobox">
          <div className="input-wrap">
            <Search className="input-icon" size={14} />
            <input className="dd-input" placeholder="Search agents…"
                   value={open ? query : (selected ? selected.name : '')}
                   onFocus={() => { setOpen(true); setQuery(''); }}
                   onBlur={() => setTimeout(() => setOpen(false), 150)}
                   onChange={e => { setQuery(e.target.value); setOpen(true); }} />
          </div>
          {open && (
            <div className="combo-list">
              {filtered.length === 0 && <div className="combo-opt" style={{ color: 'var(--muted)' }}>No agents match</div>}
              {filtered.map(a => (
                <div key={a.agentId} className={'combo-opt' + (a.agentId === agentId ? ' active' : '')}
                     onMouseDown={() => { setAgentId(a.agentId); setOpen(false); }}>
                  <AgentAvatar name={a.name} hue={agentHue(a.agentId)} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{a.name}</div>
                    {a.email && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{a.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="field-hint">Agent will be emailed when the deal is reassigned.</div>
      </div>
      <div className="field">
        <label className="field-label">Reason</label>
        <textarea className="dd-input" placeholder="e.g. Original agent on leave; transferred to keep deal moving."
                  value={reason} onChange={e => setReason(e.target.value)} rows={3} />
        <div className="field-hint">Logged to the audit trail.</div>
      </div>
    </Modal>
  );
}

function OverrideModal({ deal, onClose, onConfirm }: {
  deal: Deal; onClose: () => void; onConfirm: (d: { stage: string; reason: string }) => void;
}) {
  const [stage, setStage] = useState('');
  const [reason, setReason] = useState('');
  const valid = stage && stage !== deal.status && reason.trim().length > 0;
  return (
    <Modal title="Override stage" sub={`Manually update the stage of ${deal.dealId}`} onClose={onClose}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn" style={{ background: 'var(--violet-fg)', color: 'white', borderColor: 'var(--violet-fg)' }}
                disabled={!valid} onClick={() => onConfirm({ stage, reason })}>
          <Edit size={13} /> Override stage
        </button></>}>
      <div style={{ display: 'flex', gap: 10, padding: 10, background: 'var(--violet-bg)', border: '1px solid oklch(0.85 0.08 295)', borderRadius: 6, fontSize: 12.5, color: 'var(--violet-fg)' }}>
        <AlertTriangle size={14} />
        <div>Stage overrides are coordinator-only admin actions. Every override is fully audited and visible to the agent.</div>
      </div>
      <div className="field">
        <label className="field-label">Current stage</label>
        <div style={{ padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--line)' }}>
          <StageChip status={deal.status} />
          <span className="mono" style={{ marginLeft: 8, fontSize: 12, color: 'var(--muted)' }}>Updated {timeAgo(deal.updatedAt)}</span>
        </div>
      </div>
      <div className="field">
        <label className="field-label">New stage</label>
        <select className="dd-select" value={stage} onChange={e => setStage(e.target.value)}>
          <option value="">Select a stage…</option>
          {STAGES.filter(s => s.id !== deal.status).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field-label">Reason</label>
        <textarea className="dd-input" placeholder="Why is this manual override necessary?" value={reason}
                  onChange={e => setReason(e.target.value)} rows={3} />
        <div className="field-hint">Required — written into the audit trail.</div>
      </div>
    </Modal>
  );
}

function EmailModal({ deal, target, onClose, onConfirm }: {
  deal: Deal; target: 'client' | 'agent'; onClose: () => void;
  onConfirm: (d: { subject: string; message: string; recipient: 'client' | 'agent' }) => void;
}) {
  const recipient = target === 'client'
    ? { name: deal.clientName, email: deal.clientEmail, label: 'client' }
    : { name: deal.agentName, email: deal.agentEmail, label: 'agent' };
  const [subject, setSubject] = useState(target === 'client'
    ? `Update on your property at ${deal.suburb}`
    : `Follow-up needed: ${deal.dealId}`);
  const [body, setBody] = useState(target === 'client'
    ? `Hi ${deal.clientName.split(' ')[0]},\n\nA quick update on your transaction at ${deal.propertyAddress}. We are currently at the "${deal.stageLabel}" stage. Please let me know if you have any questions.\n\nKind regards,\nDeal Coordinator`
    : `Hi ${deal.agentName.split(' ')[0]},\n\nDeal ${deal.dealId} (${deal.clientName}, ${deal.suburb}) has been at "${deal.stageLabel}" for ${deal.stageAgeDays} days. Could you confirm the next action and an expected timeline?\n\nThanks`);
  const valid = subject.trim() && body.trim();
  return (
    <Modal title={`Email ${recipient.label}`} sub={`To ${recipient.name} · ${recipient.email || ''}`} onClose={onClose}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn accent" disabled={!valid} onClick={() => onConfirm({ subject, message: body, recipient: target })}>
          <Send size={13} /> Send email
        </button></>}>
      <div className="field">
        <label className="field-label">Subject</label>
        <input className="dd-input" value={subject} onChange={e => setSubject(e.target.value)} />
      </div>
      <div className="field">
        <label className="field-label">Message</label>
        <textarea className="dd-input" rows={9} value={body} onChange={e => setBody(e.target.value)} />
        <div className="field-hint">Sent via Amazon SES · logged to the audit trail.</div>
      </div>
    </Modal>
  );
}

function NoteModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: (d: { text: string }) => void }) {
  const [text, setText] = useState('');
  return (
    <Modal title="Add coordinator note" sub="Private — visible only to coordinators and admin" onClose={onClose}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!text.trim()} onClick={() => onConfirm({ text })}>
          <Plus size={13} /> Save note
        </button></>}>
      <div className="field">
        <label className="field-label">Note</label>
        <textarea className="dd-input" rows={6} placeholder="What happened? What did you do? What's next?"
                  value={text} onChange={e => setText(e.target.value)} autoFocus />
        <div className="field-hint">
          <Lock size={11} style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 4 }} />
          Private to coordinator/admin · agent and client cannot see this.
        </div>
      </div>
    </Modal>
  );
}

function ResolveModal({ deal, onClose, onConfirm }: {
  deal: Deal; onClose: () => void; onConfirm: (d: { note: string }) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <Modal title="Mark stagnation resolved" sub={`Clear the stagnation flag on ${deal.dealId}`} onClose={onClose}
      footer={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn" style={{ background: 'var(--good-fg)', color: 'white', borderColor: 'var(--good-fg)' }}
                onClick={() => onConfirm({ note })}>
          <Check size={13} /> Mark resolved
        </button></>}>
      <div style={{ display: 'flex', gap: 10, padding: 10, background: 'var(--stagnant-bg)', border: '1px solid var(--stagnant-line)', borderRadius: 6, fontSize: 12.5, color: 'var(--stagnant-fg)' }}>
        <AlertTriangle size={14} />
        <div>Currently stagnant for <strong>{deal.stagnantDays} days</strong>. Only clear after taking real action.</div>
      </div>
      <div className="field">
        <label className="field-label">What did you do?</label>
        <textarea className="dd-input" rows={4} placeholder="e.g. Called the conveyancer; they confirmed lodgment is happening tomorrow."
                  value={note} onChange={e => setNote(e.target.value)} />
      </div>
    </Modal>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

// ─── Date Range Picker ────────────────────────────────────────────────────────

const DATE_PRESETS = ['Today', 'Last 7 days', 'Last 14 days', 'Last 30 days', 'This quarter', 'Year to date'] as const;
type DatePreset = typeof DATE_PRESETS[number];

function DateRangePicker({ value, onChange }: { value: DatePreset; onChange: (v: DatePreset) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn" onClick={() => setOpen(o => !o)}>
        <Clock size={13} /> <span style={{ fontWeight: 500 }}>{value}</span>
      </button>
      {open && (
        <div className="combo-list" style={{ right: 0, left: 'auto', minWidth: 180 }}>
          {DATE_PRESETS.map(p => (
            <div key={p} className={'combo-opt' + (p === value ? ' active' : '')}
                 onClick={() => { onChange(p); setOpen(false); }}>
              {p === value && <Check size={11} style={{ flexShrink: 0, color: 'var(--accent)' }} />}
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Dashboard({ deals, setRoute }: { deals: Deal[]; setRoute: (r: Route) => void }) {
  const [dateRange, setDateRange] = useState<DatePreset>('Last 14 days');
  const totalValue = deals.reduce((s, d) => s + d.offerAmount, 0);
  const stagnant = deals.filter(d => d.stagnant).length;
  const atRisk = deals.filter(d => d.atRisk).length;

  const counts = STAGES.slice(0, -1).map(s => ({
    stage: s, count: deals.filter(d => d.status === s.id).length,
  }));
  const maxCount = Math.max(...counts.map(c => c.count), 1);

  const queue = [...deals].sort((a, b) => b.stagnantDays - a.stagnantDays || b.stageAgeDays - a.stageAgeDays).slice(0, 5);

  const stageValues = STAGES.slice(0, -1).map(s => ({
    stage: s, value: deals.filter(d => d.status === s.id).reduce((sum, d) => sum + d.offerAmount, 0),
  }));
  const totalSV = stageValues.reduce((s, x) => s + x.value, 0) || 1;

  // Activity: count audit entries by day (last 14 days)
  const now = Date.now();
  const dayMs = 86400000;
  const buckets = Array.from({ length: 14 }, (_, i) => {
    const dayStart = new Date(now - (13 - i) * dayMs).setHours(0, 0, 0, 0);
    return { dayStart, total: 0 };
  });
  for (const d of deals) {
    for (const a of d.auditLog) {
      const b = buckets.find(bk => a.timestamp >= bk.dayStart && a.timestamp < bk.dayStart + dayMs);
      if (b) b.total++;
    }
  }
  const maxBucket = Math.max(...buckets.map(b => b.total), 1);

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Good morning, Coordinator</h1>
          <p className="page-sub">{stagnant + atRisk} deals need your attention today · {deals.length} active</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <button className="btn"><RefreshCw size={13} /> Refresh</button>
          <button className="btn primary"><Download size={13} /> Weekly report</button>
        </div>
      </header>

      <div className="page-body">
        <div className="dash-grid kpi-first">
          {/* KPI tiles */}
          <div className="kpi-grid">
            <div className="kpi">
              <div className="kpi-label"><Folder size={13} /> Active deals</div>
              <div className="kpi-value">{deals.length}</div>
              <div className="kpi-delta"><span className="up" style={{ color: 'var(--good-fg)', display: 'flex', alignItems: 'center', gap: 4 }}><TrendingUp size={12} /></span> in pipeline</div>
              <div className="kpi-spark"><Sparkline values={[18, 22, 21, 24, 26, 28, deals.length]} w={300} h={28} /></div>
            </div>
            <div className="kpi">
              <div className="kpi-label"><Activity size={13} /> Pipeline value</div>
              <div className="kpi-value">{formatZARShort(totalValue)}</div>
              <div className="kpi-delta">total offer value</div>
              <div className="kpi-spark"><Sparkline values={[120, 140, 135, 160, 170, 182, 196]} w={300} h={28} /></div>
            </div>
            <div className="kpi alert">
              <div className="kpi-label"><AlertTriangle size={13} /> Stagnant</div>
              <div className="kpi-value">{stagnant}</div>
              <div className="kpi-delta">≥ 7 days without stage update</div>
              <div className="kpi-spark"><Sparkline values={[6, 9, 7, 11, 9, 12, stagnant]} w={300} h={28} color="oklch(0.6 0.14 55)" /></div>
            </div>
            <div className="kpi warn">
              <div className="kpi-label"><Clock size={13} /> At risk</div>
              <div className="kpi-value">{atRisk}</div>
              <div className="kpi-delta">5–6 days without update</div>
              <div className="kpi-spark"><Sparkline values={[3, 5, 4, 6, 5, 7, atRisk]} w={300} h={28} color="oklch(0.65 0.12 85)" /></div>
            </div>
          </div>

          {/* Funnel + Queue */}
          <div className="funnel-queue">
            {/* Pipeline funnel */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Pipeline funnel</div>
                  <div className="card-sub">Deal count by stage · live</div>
                </div>
              </div>
              <div className="card-body">
                <div className="funnel">
                  {counts.map(({ stage, count }) => (
                    <div className="funnel-row" key={stage.id}>
                      <div className="label">
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: stageColor(stage.hue), flexShrink: 0, display: 'inline-block' }}></span>
                        {stage.label}
                      </div>
                      <div className="bar">
                        <div className="fill" style={{ width: `${count / maxCount * 100}%`, background: `linear-gradient(90deg, ${stageColor(stage.hue)}, oklch(0.55 0.16 252))` }}></div>
                      </div>
                      <div className="count">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Priority queue */}
            <div className="card" id="priority">
              <div className="card-header">
                <div>
                  <div className="card-title">Priority queue</div>
                  <div className="card-sub">Start here — ranked by stagnation</div>
                </div>
                <button className="btn ghost sm" onClick={() => setRoute('portfolio')}>
                  View all <ChevronRight size={12} />
                </button>
              </div>
              <div className="card-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {queue.map((d, i) => (
                  <div className="queue-row" key={d.dealId} onClick={() => setRoute(`deal/${d.dealId}`)}>
                    <div className="queue-rank">{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                      <div className="qclient">{d.clientName}</div>
                      <div className="qaddress" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.suburb} · {d.city}
                      </div>
                    </div>
                    <StageChip status={d.status} />
                    <StatusPill deal={d} />
                  </div>
                ))}
                {queue.length === 0 && <div className="empty">No urgent deals — all on track.</div>}
              </div>
            </div>
          </div>

          {/* Value breakdown + Activity */}
          <div className="funnel-queue">
            {/* Value by stage */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Value by stage</div>
                  <div className="card-sub">Where the pipeline value sits today</div>
                </div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>{formatZARShort(totalSV)} total</div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', height: 10, borderRadius: 4, overflow: 'hidden', border: '1px solid var(--line)' }}>
                  {stageValues.map(({ stage, value }) => value > 0 && (
                    <div key={stage.id} title={`${stage.label}: ${formatZARShort(value)}`}
                         style={{ width: `${value / totalSV * 100}%`, background: stageColor(stage.hue) }}></div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 16px', marginTop: 14 }}>
                  {stageValues.map(({ stage, value }) => (
                    <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: stageColor(stage.hue), flexShrink: 0 }}></span>
                      <span style={{ color: 'var(--ink-2)' }}>{stage.label}</span>
                      <span className="mono" style={{ marginLeft: 'auto', color: 'var(--muted)' }}>{formatZARShort(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Activity bar chart */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Activity over time</div>
                  <div className="card-sub">Coordinator interventions, last 14 days</div>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(14, 1fr)`, gap: 4, height: 100, alignItems: 'flex-end' }}>
                  {buckets.map((b, i) => (
                    <div key={i} title={`${new Date(b.dayStart).getDate()} · ${b.total} interventions`}
                         style={{ height: b.total > 0 ? `${(b.total / maxBucket) * 100}%` : '4px', background: b.total > 0 ? 'var(--accent)' : 'var(--surface-2)', borderRadius: 3, minHeight: 4 }}></div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(14, 1fr)`, gap: 4, marginTop: 6, fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--muted)', textAlign: 'center' }}>
                  {buckets.map((b, i) => <div key={i}>{i % 3 === 0 ? new Date(b.dayStart).getDate() : ''}</div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Portfolio ────────────────────────────────────────────────────────────────

function Portfolio({ deals, setRoute, search, setSearch }: {
  deals: Deal[]; setRoute: (r: Route) => void;
  search: string; setSearch: (s: string) => void;
}) {
  const [filter, setFilter] = useState<'all' | 'stagnant' | 'atrisk' | 'ontrack'>('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [view, setView] = useState<'rows' | 'cards' | 'table'>('rows');
  const [page, setPage] = useState(1);
  const perPage = 12;

  const filtered = deals.filter(d => {
    if (filter === 'stagnant' && !d.stagnant) return false;
    if (filter === 'atrisk' && !d.atRisk) return false;
    if (filter === 'ontrack' && (d.stagnant || d.atRisk)) return false;
    if (stageFilter !== 'all' && d.status !== stageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return d.clientName.toLowerCase().includes(q) || d.agentName.toLowerCase().includes(q) ||
             d.propertyAddress.toLowerCase().includes(q) || d.dealId.toLowerCase().includes(q);
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageDeals = filtered.slice((page - 1) * perPage, page * perPage);

  const counts = {
    all: deals.length,
    stagnant: deals.filter(d => d.stagnant).length,
    atrisk: deals.filter(d => d.atRisk).length,
    ontrack: deals.filter(d => !d.stagnant && !d.atRisk).length,
  };

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Deal portfolio</h1>
          <p className="page-sub">{filtered.length} of {deals.length} active deals</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn"><Download size={13} /> Export CSV</button>
        </div>
      </header>
      <div className="page-body">
        <div className="filters">
          <div className="input-wrap">
            <Search className="input-icon" size={14} />
            <input className="dd-input" placeholder="Search client, agent, address, deal ID…"
                   value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="filter-tabs">
            {([['all', 'All'], ['stagnant', 'Stagnant'], ['atrisk', 'At risk'], ['ontrack', 'On track']] as const).map(([id, label]) => (
              <button key={id} className={filter === id ? 'active' : ''} onClick={() => { setFilter(id); setPage(1); }}>
                {label} <span className="mono" style={{ opacity: 0.6, marginLeft: 4 }}>{counts[id]}</span>
              </button>
            ))}
          </div>
          <select className="dd-select" style={{ width: 'auto' }} value={stageFilter} onChange={e => { setStageFilter(e.target.value); setPage(1); }}>
            <option value="all">All stages</option>
            {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div className="view-toggle">
            <button className={view === 'rows' ? 'active' : ''} onClick={() => setView('rows')} title="Compact rows"><AlignJustify size={14} /></button>
            <button className={view === 'cards' ? 'active' : ''} onClick={() => setView('cards')} title="Rich cards"><Grid2x2 size={14} /></button>
            <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')} title="Data table"><Table size={14} /></button>
          </div>
        </div>

        {pageDeals.length === 0 ? (
          <div className="card"><div className="empty">No deals match these filters.</div></div>
        ) : view === 'rows' ? (
          <DealRows deals={pageDeals} setRoute={setRoute} />
        ) : view === 'cards' ? (
          <DealCards deals={pageDeals} setRoute={setRoute} />
        ) : (
          <DealTableView deals={pageDeals} setRoute={setRoute} />
        )}

        <div className="pagination">
          <div>Showing <span className="mono">{(page - 1) * perPage + 1}</span>–<span className="mono">{Math.min(page * perPage, filtered.length)}</span> of <span className="mono">{filtered.length}</span></div>
          <div className="pages">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft size={12} /></button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight size={12} /></button>
          </div>
        </div>
      </div>
    </>
  );
}

function DealRows({ deals, setRoute }: { deals: Deal[]; setRoute: (r: Route) => void }) {
  return (
    <div className="deal-rows">
      <div className="deal-row row-header">
        <div>Client / Property</div>
        <div>Agent</div>
        <div>Stage</div>
        <div>Status</div>
        <div style={{ textAlign: 'right' }}>Offer (ZAR)</div>
        <div style={{ textAlign: 'right' }}>Stage age</div>
      </div>
      {deals.map(d => (
        <div className="deal-row" key={d.dealId} onClick={() => setRoute(`deal/${d.dealId}`)}>
          <div>
            <div className="row-client">
              {d.clientName}
              <span className="mono" style={{ marginLeft: 8, color: 'var(--muted)', fontSize: 11 }}>{d.dealId}</span>
            </div>
            <div className="row-address">{d.propertyAddress}</div>
          </div>
          <div className="row-agent" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AgentAvatar name={d.agentName} hue={d.agentHue} />
            {d.agentName}
          </div>
          <div><StageChip status={d.status} /></div>
          <div><StatusPill deal={d} /></div>
          <div className="row-amount" style={{ textAlign: 'right' }}>{formatZAR(d.offerAmount)}</div>
          <div className="mono" style={{ textAlign: 'right', color: 'var(--muted)', fontSize: 12 }}>{d.stageAgeDays}d</div>
        </div>
      ))}
    </div>
  );
}

function DealCards({ deals, setRoute }: { deals: Deal[]; setRoute: (r: Route) => void }) {
  return (
    <div className="deal-cards">
      {deals.map(d => (
        <div className="deal-card" key={d.dealId} onClick={() => setRoute(`deal/${d.dealId}`)}>
          <div className="dc-top">
            <div style={{ minWidth: 0 }}>
              <div className="dc-id">{d.dealId}</div>
              <div className="dc-client">{d.clientName}</div>
              <div className="dc-address">{d.propertyAddress}</div>
            </div>
            <StatusPill deal={d} />
          </div>
          <div className="dc-mid">
            <div>
              <div className="dc-amount-label">Offer value</div>
              <div className="dc-amount">{formatZAR(d.offerAmount)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="dc-amount-label">Stage age</div>
              <div className="dc-amount mono">{d.stageAgeDays}<span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 2 }}>d</span></div>
            </div>
          </div>
          <div className="dc-bottom">
            <div className="dc-agent">
              <AgentAvatar name={d.agentName} hue={d.agentHue} />
              {d.agentName}
            </div>
            <StageChip status={d.status} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DealTableView({ deals, setRoute }: { deals: Deal[]; setRoute: (r: Route) => void }) {
  return (
    <div style={{ overflow: 'auto' }}>
      <table className="deal-table">
        <thead>
          <tr>
            <th>Deal ID</th><th>Client</th><th>Address</th><th>Agent</th>
            <th>Stage</th><th>Status</th>
            <th style={{ textAlign: 'right' }}>Offer (ZAR)</th>
            <th style={{ textAlign: 'right' }}>Age</th>
            <th style={{ textAlign: 'right' }}>Updated</th>
          </tr>
        </thead>
        <tbody>
          {deals.map(d => (
            <tr key={d.dealId} onClick={() => setRoute(`deal/${d.dealId}`)}>
              <td className="id-col">{d.dealId}</td>
              <td style={{ fontWeight: 500 }}>{d.clientName}</td>
              <td style={{ color: 'var(--muted)' }}>{d.propertyAddress}</td>
              <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><AgentAvatar name={d.agentName} hue={d.agentHue} size={18} />{d.agentName}</span></td>
              <td><StageChip status={d.status} /></td>
              <td><StatusPill deal={d} /></td>
              <td className="num" style={{ textAlign: 'right', fontWeight: 500 }}>{formatZAR(d.offerAmount)}</td>
              <td className="num" style={{ textAlign: 'right', color: 'var(--muted)' }}>{d.stageAgeDays}d</td>
              <td className="num" style={{ textAlign: 'right', color: 'var(--muted)' }}>{timeAgo(d.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Pipeline (Kanban) ────────────────────────────────────────────────────────

function Pipeline({ deals, setRoute, onStageChange }: {
  deals: Deal[]; setRoute: (r: Route) => void;
  onStageChange: (deal: Deal, newStage: string) => Promise<void>;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const toast = useToast();

  const byStage = STAGES.reduce((acc, s) => {
    acc[s.id] = deals.filter(d => d.status === s.id);
    return acc;
  }, {} as Record<string, Deal[]>);

  const onDrop = async (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    const deal = deals.find(d => d.dealId === dragId);
    if (!deal || deal.status === stageId) { setDragId(null); setOverStage(null); return; }
    setDragId(null); setOverStage(null);
    try {
      await onStageChange(deal, stageId);
      toast(`${deal.clientName} → ${getStage(stageId).label}`);
    } catch { toast('Failed to update stage'); }
  };

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Pipeline</h1>
          <p className="page-sub">Drag a deal between stages to update it · {deals.length} active deals</p>
        </div>
      </header>
      <div className="pipeline-body">
        <div className="kanban">
          {STAGES.slice(0, -1).map(stage => {
            const list = byStage[stage.id] || [];
            const totalValue = list.reduce((s, d) => s + d.offerAmount, 0);
            return (
              <div key={stage.id}
                   className={'kanban-col' + (overStage === stage.id && dragId ? ' over' : '')}
                   onDragOver={e => { e.preventDefault(); setOverStage(stage.id); }}
                   onDragLeave={() => setOverStage(null)}
                   onDrop={e => onDrop(e, stage.id)}>
                <div className="kanban-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: stageColor(stage.hue) }}></span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{stage.label}</span>
                    <span className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>{list.length}</span>
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{formatZARShort(totalValue)}</div>
                </div>
                <div className="kanban-list">
                  {list.length === 0 && <div className="kanban-empty">No deals here</div>}
                  {list.map(d => (
                    <div key={d.dealId}
                         className={'kanban-card' + (dragId === d.dealId ? ' dragging' : '')}
                         draggable
                         onDragStart={e => { setDragId(d.dealId); e.dataTransfer.effectAllowed = 'move'; }}
                         onDragEnd={() => { setDragId(null); setOverStage(null); }}
                         onClick={() => setRoute(`deal/${d.dealId}`)}>
                      <div className="kc-top">
                        <div className="dc-id">{d.dealId}</div>
                        <StatusPill deal={d} />
                      </div>
                      <div className="kc-client">{d.clientName}</div>
                      <div className="kc-address">{d.suburb} · {d.city}</div>
                      <div className="kc-foot">
                        <div className="kc-amount">{formatZARShort(d.offerAmount)}</div>
                        <AgentAvatar name={d.agentName} hue={d.agentHue} size={20} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Escalations (mock data — no API endpoint yet) ────────────────────────────

const ESC_SEED = [
  { id: 'ESC-0014', severity: 'high' as const, status: 'open' as const, raisedTo: 'Principal · Sandton',
    raisedAt: Date.now() - 2 * 86400000, reason: "Conveyancer non-responsive for 12 days despite multiple follow-ups. Buyer's bond expires Friday.", ack: false },
  { id: 'ESC-0013', severity: 'medium' as const, status: 'acknowledged' as const, raisedTo: 'Director · Sea Point',
    raisedAt: Date.now() - 5 * 86400000, ackAt: Date.now() - 4 * 86400000, reason: 'Seller refusing to vacate by occupation date. Need legal review of OTP terms.', ack: true },
  { id: 'ESC-0012', severity: 'high' as const, status: 'resolved' as const, raisedTo: 'Principal · Sandton',
    raisedAt: Date.now() - 9 * 86400000, ackAt: Date.now() - 8 * 86400000, resolvedAt: Date.now() - 3 * 86400000,
    reason: 'Bond originator made critical error on submission. Required escalation to bank manager directly.',
    resolution: 'Bank reissued AIP with corrected terms within 48h. Deal advanced.', ack: true },
  { id: 'ESC-0011', severity: 'low' as const, status: 'open' as const, raisedTo: 'Principal · Rosebank',
    raisedAt: Date.now() - 1 * 86400000, reason: 'Agent reassignment dispute — original agent claiming commission split. Need director ruling.', ack: false },
];

function Escalations({ deals }: { deals: Deal[] }) {
  const [filter, setFilter] = useState<'open' | 'acknowledged' | 'resolved' | 'all'>('open');
  const toast = useToast();

  const items = ESC_SEED.map((e, i) => ({ ...e, deal: deals[i] || deals[0] })).filter(Boolean);
  const filtered = filter === 'all' ? items : items.filter(e => e.status === filter);
  const counts = { open: items.filter(i => i.status === 'open').length, acknowledged: items.filter(i => i.status === 'acknowledged').length, resolved: items.filter(i => i.status === 'resolved').length, all: items.length };

  const sevPill: Record<string, { cls: string; label: string }> = {
    high:   { cls: 'stagnant', label: 'High severity' },
    medium: { cls: 'risk',     label: 'Medium' },
    low:    { cls: 'accent',   label: 'Low' },
  };
  const statusPill: Record<string, { cls: string; label: string }> = {
    open:         { cls: 'stagnant', label: 'Open · awaiting ack' },
    acknowledged: { cls: 'accent',   label: 'Acknowledged' },
    resolved:     { cls: 'good',     label: 'Resolved' },
  };

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Escalations</h1>
          <p className="page-sub">Formal escalations to principals / directors when a deal can't be resolved at coordinator level</p>
        </div>
        <button className="btn primary" onClick={() => toast("Raise escalation — coming in next sprint")}><Plus size={13} /> Raise escalation</button>
      </header>
      <div className="page-body">
        <div className="filters">
          <div className="filter-tabs">
            {(['open', 'acknowledged', 'resolved', 'all'] as const).map(id => (
              <button key={id} className={filter === id ? 'active' : ''} onClick={() => setFilter(id)}>
                {id.charAt(0).toUpperCase() + id.slice(1)} <span className="mono" style={{ opacity: 0.6, marginLeft: 4 }}>{counts[id]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="esc-grid">
          {filtered.length === 0 && <div className="card"><div className="empty">No escalations in this view.</div></div>}
          {filtered.map(e => {
            const sev = sevPill[e.severity];
            const st = statusPill[e.status];
            return (
              <div key={e.id} className="card esc-card">
                <div className="esc-top">
                  <div>
                    <div className="esc-id">{e.id}</div>
                    <h3 className="esc-title">{e.deal?.clientName || 'Deal'} · {e.deal?.suburb || ''}</h3>
                    {e.deal && <div className="esc-meta"><span className="mono">{e.deal.dealId}</span><span>·</span><StageChip status={e.deal.status} /><span>·</span><span className="mono">{formatZARShort(e.deal.offerAmount)}</span></div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    <span className={'pill ' + sev.cls}><span className="dot"></span>{sev.label}</span>
                    <span className={'pill ' + st.cls}><span className="dot"></span>{st.label}</span>
                  </div>
                </div>
                <div className="esc-reason">{e.reason}</div>
                {'resolution' in e && e.resolution && (
                  <div className="esc-resolution">
                    <div style={{ fontSize: 11, color: 'var(--good-fg)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 4 }}>Resolution</div>
                    {e.resolution}
                  </div>
                )}
                <div className="esc-trail">
                  <div className="esc-trail-row">
                    <div className="esc-trail-marker" style={{ background: 'var(--surface-2)' }}><Flag size={11} /></div>
                    <div>Raised by <strong>Deal Coordinator</strong> to <strong>{e.raisedTo}</strong></div>
                    <div className="esc-trail-time">{fullDate(e.raisedAt)}</div>
                  </div>
                  {'ackAt' in e && e.ackAt && (
                    <div className="esc-trail-row">
                      <div className="esc-trail-marker" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><Check size={11} /></div>
                      <div>Acknowledged by <strong>{e.raisedTo.split('·')[0].trim()}</strong></div>
                      <div className="esc-trail-time">{fullDate(e.ackAt)}</div>
                    </div>
                  )}
                  {'resolvedAt' in e && e.resolvedAt && (
                    <div className="esc-trail-row">
                      <div className="esc-trail-marker" style={{ background: 'var(--good-bg)', color: 'var(--good-fg)' }}><Check size={11} /></div>
                      <div>Resolved</div>
                      <div className="esc-trail-time">{fullDate(e.resolvedAt)}</div>
                    </div>
                  )}
                </div>
                <div className="esc-actions">
                  {e.status === 'open' && <button className="btn sm" onClick={() => toast('Reminder sent to ' + e.raisedTo.split('·')[0].trim())}><Bell size={12} /> Nudge</button>}
                  {e.status !== 'resolved' && <button className="btn sm" onClick={() => toast('Marked resolved')}><Check size={12} /> Mark resolved</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Deal Detail ──────────────────────────────────────────────────────────────

function DealDetail({ deal, setRoute, agents, onUpdate }: {
  deal: Deal; setRoute: (r: Route) => void; agents: AgentOption[];
  onUpdate: (updated: Deal) => void;
}) {
  const [tab, setTab] = useState<'actions' | 'notes' | 'audit'>('actions');
  const [openModal, setOpenModal] = useState<'reassign' | 'override' | 'email-client' | 'email-agent' | 'note' | 'resolve' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const close = () => setOpenModal(null);

  const newAuditEntry = (type: string, details: AuditEntry['details']): AuditEntry => ({
    id: 'au-' + Math.random().toString(36).slice(2, 9),
    type, details, timestamp: Date.now(),
    actor: 'Deal Coordinator', actorRole: 'Deal Coordinator',
  });

  const doReassign = async ({ agentId, reason }: { agentId: string; reason: string }) => {
    setSubmitting(true);
    try {
      await apiCall(`/dealdesk/deals/${deal.dealId}/reassign`, { method: 'PUT', body: JSON.stringify({ newAgentId: agentId, reason }) });
      const ag = agents.find(a => a.agentId === agentId);
      onUpdate({
        ...deal, agentId, agentName: ag?.name || agentId, agentEmail: ag?.email || '',
        agentHue: agentHue(agentId),
        auditLog: [newAuditEntry('reassign', { reason, from: deal.agentName, to: ag?.name || agentId }), ...deal.auditLog],
      });
      close(); toast(`Reassigned to ${ag?.name || agentId}`);
    } catch (err: any) { toast(err.message); }
    finally { setSubmitting(false); }
  };

  const doOverride = async ({ stage, reason }: { stage: string; reason: string }) => {
    setSubmitting(true);
    try {
      await apiCall(`/dealdesk/deals/${deal.dealId}/status`, { method: 'PUT', body: JSON.stringify({ newStatus: stage, reason }) });
      const s = getStage(stage);
      onUpdate({
        ...deal, status: stage, stageLabel: s.label, stageHue: s.hue,
        stageAgeDays: 0, stagnant: false, stagnantDays: 0, atRisk: false, updatedAt: Date.now(),
        auditLog: [newAuditEntry('override', { reason, from: deal.stageLabel, to: s.label }), ...deal.auditLog],
      });
      close(); toast(`Stage overridden to ${s.label}`);
    } catch (err: any) { toast(err.message); }
    finally { setSubmitting(false); }
  };

  const doEmail = async ({ subject, message, recipient }: { subject: string; message: string; recipient: 'client' | 'agent' }) => {
    setSubmitting(true);
    try {
      await apiCall(`/dealdesk/deals/${deal.dealId}/contact`, { method: 'POST', body: JSON.stringify({ recipient, channel: 'email', subject, message }) });
      onUpdate({ ...deal, auditLog: [newAuditEntry('contact', { target: recipient, subject }), ...deal.auditLog] });
      close(); toast(`Email sent to ${recipient}`);
    } catch (err: any) { toast(err.message); }
    finally { setSubmitting(false); }
  };

  const doNote = async ({ text }: { text: string }) => {
    setSubmitting(true);
    try {
      const res = await apiCall(`/dealdesk/deals/${deal.dealId}/notes`, { method: 'POST', body: JSON.stringify({ note: text }) });
      const newNote: NoteEntry = res.data?.note
        ? normalizeNote(res.data.note, deal.coordinatorNotes.length)
        : { id: 'n-' + Math.random().toString(36).slice(2, 9), author: 'Deal Coordinator', timestamp: Date.now(), text };
      onUpdate({
        ...deal,
        coordinatorNotes: [newNote, ...deal.coordinatorNotes],
        auditLog: [newAuditEntry('note', { preview: text.slice(0, 60) }), ...deal.auditLog],
      });
      close(); toast('Note added'); setTab('notes');
    } catch (err: any) { toast(err.message); }
    finally { setSubmitting(false); }
  };

  const doResolve = async ({ note }: { note: string }) => {
    setSubmitting(true);
    try {
      await apiCall(`/dealdesk/deals/${deal.dealId}/resolve-stagnation`, { method: 'POST' });
      onUpdate({
        ...deal, stagnant: false, stagnantDays: 0, atRisk: false,
        auditLog: [newAuditEntry('stagnation_resolved', { note }), ...deal.auditLog],
      });
      close(); toast('Stagnation cleared');
    } catch (err: any) { toast(err.message); }
    finally { setSubmitting(false); }
  };

  const auditMeta: Record<string, { label: string; icon: React.FC<any>; fg: string; bg: string }> = {
    reassign:            { label: 'Reassigned deal',         icon: ArrowLeftRight, fg: 'var(--accent)',     bg: 'var(--accent-soft)' },
    override:            { label: 'Overrode stage',          icon: Edit,           fg: 'var(--violet-fg)',  bg: 'var(--violet-bg)' },
    status_override:     { label: 'Overrode stage',          icon: Edit,           fg: 'var(--violet-fg)',  bg: 'var(--violet-bg)' },
    contact:             { label: 'Contacted party',         icon: Mail,           fg: 'var(--good-fg)',    bg: 'var(--good-bg)' },
    stagnation_resolved: { label: 'Cleared stagnation flag', icon: CheckCircle2,   fg: 'var(--good-fg)',    bg: 'var(--good-bg)' },
    note:                { label: 'Added note',              icon: StickyNote,     fg: 'var(--ink-2)',      bg: 'var(--surface-2)' },
    note_added:          { label: 'Added note',              icon: StickyNote,     fg: 'var(--ink-2)',      bg: 'var(--surface-2)' },
  };

  const tiles = [
    { id: 'reassign',    title: 'Reassign deal',           desc: 'Move this deal to a different agent', icon: ArrowLeftRight, bg: 'var(--accent-soft)', fg: 'var(--accent)' },
    { id: 'override',    title: 'Override stage',          desc: 'Manually update the deal stage',      icon: Edit,           bg: 'var(--violet-bg)',   fg: 'var(--violet-fg)' },
    { id: 'email-client',title: 'Email client',            desc: `Message ${deal.clientName.split(' ')[0]} directly`,       icon: Mail, bg: 'var(--good-bg)', fg: 'var(--good-fg)' },
    { id: 'email-agent', title: 'Email agent',             desc: `Follow up with ${deal.agentName.split(' ')[0]}`,          icon: Send, bg: 'var(--good-bg)', fg: 'var(--good-fg)' },
    { id: 'note',        title: 'Add note',                desc: 'Private coordinator note on this deal',icon: StickyNote,    bg: 'var(--surface-2)',   fg: 'var(--ink-2)' },
    deal.stagnant ? { id: 'resolve', title: 'Mark stagnation resolved', desc: `Clear the ${deal.stagnantDays}-day stagnation flag`, icon: CheckCircle2, bg: 'var(--stagnant-bg)', fg: 'var(--stagnant-fg)' } : null,
  ].filter(Boolean) as { id: string; title: string; desc: string; icon: React.FC<any>; bg: string; fg: string }[];

  const idx = STAGES.findIndex(s => s.id === deal.status);

  return (
    <>
      <div className="detail-header">
        <div className="crumbs">
          <span onClick={() => setRoute('portfolio')} style={{ cursor: 'pointer' }}>Portfolio</span>
          <ChevronRight size={12} />
          <span style={{ color: 'var(--ink-2)' }}>{deal.dealId}</span>
        </div>
        <div className="detail-top">
          <div style={{ minWidth: 0 }}>
            <h1 className="detail-title">
              {deal.clientName}
              <span className="detail-id">· {deal.dealId}</span>
            </h1>
            <div className="detail-address"><MapPin size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />{deal.propertyAddress}</div>
            <div className="detail-meta">
              <div className="item"><span className="lbl">Offer</span><span className="val mono">{formatZAR(deal.offerAmount)}</span></div>
              <div className="item"><span className="lbl">Stage</span><span className="val"><StageChip status={deal.status} /></span></div>
              <div className="item"><span className="lbl">Status</span><span className="val"><StatusPill deal={deal} /></span></div>
              <div className="item"><span className="lbl">Agent</span><span className="val" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><AgentAvatar name={deal.agentName} hue={deal.agentHue} size={20} />{deal.agentName}</span></div>
              <div className="item"><span className="lbl">Stage age</span><span className="val mono">{deal.stageAgeDays}d</span></div>
              <div className="item"><span className="lbl">Updated</span><span className="val">{timeAgo(deal.updatedAt)}</span></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn" onClick={() => setOpenModal('email-agent')}><Mail size={13} /> Email agent</button>
            <button className="btn primary" onClick={() => setOpenModal('reassign')}><ArrowLeftRight size={13} /> Reassign</button>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button className={'tab' + (tab === 'actions' ? ' active' : '')} onClick={() => setTab('actions')}><Zap size={14} /> Actions</button>
        <button className={'tab' + (tab === 'notes' ? ' active' : '')} onClick={() => setTab('notes')}><StickyNote size={14} /> Notes <span className="count">{deal.coordinatorNotes.length}</span></button>
        <button className={'tab' + (tab === 'audit' ? ' active' : '')} onClick={() => setTab('audit')}><History size={14} /> Audit <span className="count">{deal.auditLog.length}</span></button>
      </div>

      <div className="detail-body">
        <div className="detail-grid">
          <div>
            {tab === 'actions' && (
              <>
                <div className="card">
                  <div className="card-header">
                    <div><div className="card-title">Coordinator actions</div><div className="card-sub">Every action is recorded in the audit trail.</div></div>
                  </div>
                  <div className="card-body">
                    <div className="action-grid">
                      {tiles.map(t => (
                        <button key={t.id} className="action-tile" disabled={submitting} onClick={() => setOpenModal(t.id as any)}>
                          <div className="icon-wrap" style={{ background: t.bg, color: t.fg }}><t.icon size={16} /></div>
                          <div><h4>{t.title}</h4><p>{t.desc}</p></div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 16 }} className="card">
                  <div className="card-header"><div><div className="card-title">Stage timeline</div><div className="card-sub">SA conveyancing stages</div></div></div>
                  <div className="card-body">
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
                      {STAGES.map((s, i) => {
                        const done = i < idx, active = i === idx;
                        return (
                          <div key={s.id} style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ height: 6, borderRadius: 3, background: (done || active) ? stageColor(s.hue) : 'var(--surface-2)', border: '1px solid ' + ((active || done) ? 'transparent' : 'var(--line)'), opacity: done ? 0.55 : 1 }}></div>
                            <div style={{ marginTop: 8, fontSize: 11.5, fontWeight: active ? 600 : 400, color: active ? 'var(--ink)' : 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
            {tab === 'notes' && (
              <div className="card">
                <div className="card-header">
                  <div><div className="card-title">Coordinator notes</div><div className="card-sub"><Lock size={11} style={{ verticalAlign: '-1px' }} /> Private to coordinator/admin only.</div></div>
                  <button className="btn primary sm" onClick={() => setOpenModal('note')}><Plus size={13} /> Add note</button>
                </div>
                <div className="card-body">
                  {deal.coordinatorNotes.length === 0 ? <div className="empty">No notes yet.</div> : deal.coordinatorNotes.map(n => (
                    <div key={n.id} className="note">
                      <div className="note-head">
                        <div className="dd-avatar" style={{ width: 22, height: 22, fontSize: 9 }}>{initials(n.author)}</div>
                        <div className="note-author">{n.author}</div>
                        <div className="note-time">· {fullDate(n.timestamp)} · {timeAgo(n.timestamp)}</div>
                      </div>
                      <div className="note-body">{n.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === 'audit' && (
              <div className="card">
                <div className="card-header">
                  <div><div className="card-title">Audit trail</div><div className="card-sub">{deal.auditLog.length} coordinator interventions</div></div>
                </div>
                <div className="card-body">
                  {deal.auditLog.length === 0 ? <div className="empty">No coordinator interventions yet.</div> : (
                    <div className="timeline">
                      {deal.auditLog.map(a => {
                        const m = auditMeta[a.type] || auditMeta.note;
                        const d = a.details || {};
                        return (
                          <div className="audit-entry" key={a.id}>
                            <div className="marker" style={{ background: m.bg, color: m.fg, borderColor: m.bg }}><m.icon size={12} /></div>
                            <div className="audit-head">
                              <span className="audit-actor">{a.actor}</span>
                              <span className="audit-action">{m.label.toLowerCase()}</span>
                              <span className="audit-time">{fullDate(a.timestamp)} · {timeAgo(a.timestamp)}</span>
                            </div>
                            {(d.reason || d.note || d.subject || d.preview || (d.from && d.to)) && (
                              <div className="audit-detail">
                                {d.from && d.to && <div style={{ marginBottom: d.reason ? 6 : 0 }}><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{d.from}</span><span style={{ color: 'var(--muted-2)', margin: '0 6px' }}>→</span><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{d.to}</span></div>}
                                {d.target && d.subject && <div style={{ marginBottom: 4 }}>To <strong>{d.target}</strong> · "{d.subject}"</div>}
                                {d.reason && <div>{d.reason}</div>}
                                {d.note && <div>{d.note}</div>}
                                {d.preview && <div style={{ fontStyle: 'italic' }}>"{d.preview}{d.preview.length >= 60 ? '…' : ''}"</div>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="card">
            <div className="card-header"><div className="card-title">Parties & details</div></div>
            <div className="card-body">
              <div className="side-panel-section">
                <h5>Buyer</h5>
                <dl className="kv">
                  <dt>Name</dt><dd>{deal.clientName}</dd>
                  <dt>Email</dt><dd className="mono" style={{ fontSize: 12 }}>{deal.clientEmail || '—'}</dd>
                </dl>
              </div>
              <div className="side-panel-section">
                <h5>Agent</h5>
                <dl className="kv">
                  <dt>Name</dt><dd>{deal.agentName}</dd>
                  <dt>Email</dt><dd className="mono" style={{ fontSize: 12 }}>{deal.agentEmail || '—'}</dd>
                </dl>
              </div>
              <div className="side-panel-section">
                <h5>Property</h5>
                <dl className="kv">
                  <dt>Address</dt><dd>{deal.propertyAddress}</dd>
                  <dt>Suburb</dt><dd>{deal.suburb}</dd>
                  <dt>City</dt><dd>{deal.city}</dd>
                </dl>
              </div>
              <div className="side-panel-section">
                <h5>Deal</h5>
                <dl className="kv">
                  <dt>Deal ID</dt><dd className="mono">{deal.dealId}</dd>
                  <dt>Offer</dt><dd className="mono">{formatZAR(deal.offerAmount)}</dd>
                  <dt>Created</dt><dd>{fullDate(deal.createdAt)}</dd>
                  <dt>Updated</dt><dd>{fullDate(deal.updatedAt)}</dd>
                </dl>
              </div>
              <div className="side-panel-section" style={{ paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                <h5>Phase 2 — Parties (preview)</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: 0.55 }}>
                  {['Conveyancer', 'Bond originator', 'Bank', 'Seller'].map(p => (
                    <div key={p} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', color: 'var(--muted)' }}>
                      <span>{p}</span><span className="nav-badge" style={{ fontSize: 9 }}>Soon</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openModal === 'reassign'    && <ReassignModal deal={deal} agents={agents} onClose={close} onConfirm={doReassign} />}
      {openModal === 'override'    && <OverrideModal deal={deal} onClose={close} onConfirm={doOverride} />}
      {openModal === 'email-client' && <EmailModal deal={deal} target="client" onClose={close} onConfirm={doEmail} />}
      {openModal === 'email-agent'  && <EmailModal deal={deal} target="agent" onClose={close} onConfirm={doEmail} />}
      {openModal === 'note'        && <NoteModal onClose={close} onConfirm={doNote} />}
      {openModal === 'resolve'     && <ResolveModal deal={deal} onClose={close} onConfirm={doResolve} />}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface DealDeskHubProps {
  onBackToCatalog: () => void;
}

export function DealDeskHub({ onBackToCatalog }: DealDeskHubProps) {
  const [route, setRoute] = useState<Route>('dashboard');
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [agents, setAgents] = useState<AgentOption[]>([]);

  // Load all deals
  const loadDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/dealdesk/deals?page=1&limit=100');
      const raw: RawDeal[] = res.data?.deals || res.deals || [];
      setDeals(raw.map(augmentDeal));
    } catch (err: any) {
      console.error('DealDesk load failed:', err.message);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load audit + notes for a specific deal
  const loadDealAudit = useCallback(async (dealId: string) => {
    try {
      const res = await apiCall(`/dealdesk/deals/${dealId}/audit`);
      const rawAudit: any[] = res.data?.auditLog || res.auditLog || [];
      const rawNotes: any[] = res.data?.coordinatorNotes || res.coordinatorNotes || [];
      setDeals(prev => prev.map(d => d.dealId === dealId ? {
        ...d,
        auditLog: rawAudit.map(normalizeAuditEntry),
        coordinatorNotes: rawNotes.map(normalizeNote),
      } : d));
    } catch {
      // silent — deal detail still renders without audit
    }
  }, []);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  // Load agents for reassign
  useEffect(() => {
    apiCall('/agents')
      .then(res => setAgents(res.data?.agents || res.agents || []))
      .catch(() => {});
  }, []);

  // When navigating to a deal detail, load its audit
  useEffect(() => {
    const m = route.match(/^deal\/(.+)$/);
    if (m) {
      const dealId = m[1];
      const deal = deals.find(d => d.dealId === dealId);
      if (deal && deal.auditLog.length === 0) {
        loadDealAudit(dealId);
      }
    }
  }, [route, deals, loadDealAudit]);

  const updateDeal = useCallback((updated: Deal) => {
    setDeals(prev => prev.map(d => d.dealId === updated.dealId ? updated : d));
  }, []);

  const handleStageChange = useCallback(async (deal: Deal, newStage: string) => {
    await apiCall(`/dealdesk/deals/${deal.dealId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ newStatus: newStage, reason: 'Moved via pipeline drag' }),
    });
    const s = getStage(newStage);
    updateDeal({
      ...deal, status: newStage, stageLabel: s.label, stageHue: s.hue,
      stageAgeDays: 0, stagnant: false, stagnantDays: 0, atRisk: false, updatedAt: Date.now(),
    });
  }, [updateDeal]);

  // Route matching
  const dealMatch = route.match(/^deal\/(.+)$/);
  const selectedDeal = dealMatch ? deals.find(d => d.dealId === dealMatch[1]) : null;

  const renderScreen = () => {
    if (loading) return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--muted)', fontSize: 13 }}>
        Loading deals…
      </div>
    );
    if (dealMatch) {
      if (!selectedDeal) return <div className="page-body"><div className="card"><div className="empty">Deal not found. <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setRoute('portfolio')}>Back to portfolio</span></div></div></div>;
      return <DealDetail deal={selectedDeal} setRoute={setRoute} agents={agents} onUpdate={updateDeal} />;
    }
    if (route === 'pipeline') return <Pipeline deals={deals} setRoute={setRoute} onStageChange={handleStageChange} />;
    if (route === 'escalations') return <Escalations deals={deals} />;
    if (route === 'portfolio' || route.startsWith('portfolio')) return <Portfolio deals={deals} setRoute={setRoute} search={search} setSearch={setSearch} />;
    return <Dashboard deals={deals} setRoute={setRoute} />;
  };

  return (
    <DDToastProvider>
      <div className="dealdesk-wrap">
        <NavTop route={route} setRoute={setRoute} search={search} setSearch={setSearch} onBack={onBackToCatalog} />
        <main className="main">
          {renderScreen()}
        </main>
      </div>
    </DDToastProvider>
  );
}
